import { useI18n, localeMeta, type Locale } from "../i18n";
import { useAuthStore } from "@veorest/api-client";
import { Globe, Moon, Sun, LogOut, Info } from "lucide-react";

const LANGUAGES: Locale[] = ["en", "ar", "ku", "ms", "zh"];

export default function SettingsPage({ dark, toggleDark, onSignOut }: { dark: boolean; toggleDark: () => void; onSignOut: () => void }) {
  const { t, locale, setLocale } = useI18n();
  const { user } = useAuthStore();

  return (
    <div style={{ height: "100%", overflowY: "auto", background: "var(--surface-muted)" }}>
      <div style={{ maxWidth: 600, margin: "0 auto", padding: "24px 20px" }}>
        <h1 style={{ fontWeight: 900, fontSize: 22, color: "var(--text-primary)", margin: "0 0 24px" }}>{t("settings.title")}</h1>

        {/* Language */}
        <div style={{ background: "var(--surface-raised)", borderRadius: "var(--radius-lg)", border: "1px solid var(--border-subtle)", padding: "18px 20px", marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: "var(--accent-light)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Globe size={20} style={{ color: "var(--accent)" }} />
            </div>
            <div>
              <p style={{ margin: 0, fontWeight: 800, fontSize: 15, color: "var(--text-primary)" }}>{t("settings.language")}</p>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: "var(--text-muted)" }}>{t("settings.languageDesc")}</p>
            </div>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {LANGUAGES.map((lang) => {
              const meta = localeMeta[lang];
              const active = locale === lang;
              return (
                <button
                  key={lang}
                  onClick={() => setLocale(lang)}
                  style={{
                    padding: "10px 18px", borderRadius: "var(--radius-md)", border: active ? "2px solid var(--accent)" : "1px solid var(--border)",
                    background: active ? "var(--accent-light)" : "var(--surface-raised)",
                    color: active ? "var(--accent)" : "var(--text-secondary)",
                    fontWeight: 700, fontSize: 14, cursor: "pointer",
                    display: "flex", alignItems: "center", gap: 8,
                  }}
                >
                  <span style={{ fontSize: 18 }}>{lang === "ar" ? "🇮🇶" : lang === "ku" ? "🏴" : lang === "ms" ? "🇲🇾" : lang === "zh" ? "🇨🇳" : "🇬🇧"}</span>
                  <span>{meta.nativeName}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Theme */}
        <div style={{ background: "var(--surface-raised)", borderRadius: "var(--radius-lg)", border: "1px solid var(--border-subtle)", padding: "18px 20px", marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: "var(--accent-light)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {dark ? <Moon size={20} style={{ color: "var(--accent)" }} /> : <Sun size={20} style={{ color: "var(--accent)" }} />}
            </div>
            <div>
              <p style={{ margin: 0, fontWeight: 800, fontSize: 15, color: "var(--text-primary)" }}>{t("settings.theme")}</p>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: "var(--text-muted)" }}>{t("settings.themeDesc")}</p>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={toggleDark} style={{ padding: "10px 24px", borderRadius: "var(--radius-md)", border: !dark ? "2px solid var(--accent)" : "1px solid var(--border)", background: !dark ? "var(--accent-light)" : "var(--surface-raised)", color: !dark ? "var(--accent)" : "var(--text-secondary)", fontWeight: 700, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}><Sun size={16} />{t("settings.light")}</button>
            <button onClick={toggleDark} style={{ padding: "10px 24px", borderRadius: "var(--radius-md)", border: dark ? "2px solid var(--accent)" : "1px solid var(--border)", background: dark ? "var(--accent-light)" : "var(--surface-raised)", color: dark ? "var(--accent)" : "var(--text-secondary)", fontWeight: 700, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}><Moon size={16} />{t("settings.dark")}</button>
          </div>
        </div>

        {/* Account */}
        <div style={{ background: "var(--surface-raised)", borderRadius: "var(--radius-lg)", border: "1px solid var(--border-subtle)", padding: "18px 20px", marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: "var(--accent-light)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontWeight: 800, fontSize: 16, color: "var(--accent)" }}>
              {user?.email?.charAt(0).toUpperCase() ?? "?"}
            </div>
            <div>
              <p style={{ margin: 0, fontWeight: 800, fontSize: 15, color: "var(--text-primary)" }}>{t("settings.account")}</p>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: "var(--text-muted)" }}>{t("settings.accountDesc")}: {user?.email ?? ""}</p>
            </div>
          </div>
          <button onClick={onSignOut} style={{ padding: "10px 24px", borderRadius: "var(--radius-md)", border: "1px solid var(--danger)", background: "transparent", color: "var(--danger)", fontWeight: 700, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
            <LogOut size={16} />{t("settings.signOut")}
          </button>
        </div>

        {/* About */}
        <div style={{ background: "var(--surface-raised)", borderRadius: "var(--radius-lg)", border: "1px solid var(--border-subtle)", padding: "18px 20px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: "var(--accent-light)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Info size={20} style={{ color: "var(--accent)" }} />
            </div>
            <div>
              <p style={{ margin: 0, fontWeight: 800, fontSize: 15, color: "var(--text-primary)" }}>{t("settings.about")}</p>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: "var(--text-muted)" }}>{t("settings.aboutDesc")} v1.0</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
